/**
 * @jsxRuntime classic
 * @jsx jsx
 * @jsxFrag jsx
 */

import { cssMap, cx, jsx } from '@atlaskit/css';
import type { TriggerProps } from '@atlaskit/popup/types';
import { Box, Inline, Text } from '@atlaskit/primitives/compiled';
import Suspense from '@atlaskit/react-ufo/suspense';
import { token } from '@atlaskit/tokens';

import { ShimmerText } from '../../compact-research-steps/ShimmerText';
import { getBorderColor, getIconColor } from '../skill-colors';
import type { SkillIconComponent } from '../skill-icons';
import type { RovoSkillTagProps } from './SkillTag';

const styles = cssMap({
	parallelogram: {
		marginLeft: token('space.050'),
		paddingLeft: token('space.075'),
		paddingRight: token('space.075'),
		borderRadius: token('radius.xsmall'),
		// to create a parallelogram, skew the span -15 degrees
		transform: 'skewX(-15deg)',
		display: 'inline-block',
	},
	background: {
		backgroundColor: token('color.background.neutral'),
	},
	backgroundInverse: {
		backgroundColor: token('color.background.inverse.subtle'),
	},
	parallelogramWithWrapping: {
		boxSizing: 'border-box',
		boxDecorationBreak: 'clone',
		display: 'inline',
		transform: 'none',
	},
	borderAccent: {
		borderLeftStyle: 'solid',
		borderLeftWidth: token('border.width.selected'),
	},
	content: {
		// skew the contents of the span back to normal orientation so that only the outer element is skewed
		transform: 'skewX(15deg)',
	},
	contentWithWrapping: {
		display: 'inline',
		transform: 'none',
		whiteSpace: 'pre-wrap',
		wordBreak: 'break-all',
	},
	labelWithWrapping: {
		display: 'inline',
	},
	slashWithWrapping: {
		display: 'inline',
		marginRight: token('space.050'),
	},
	iconWithWrapping: {
		display: 'inline-flex',
		marginRight: token('space.050'),
		transform: 'translateY(-1px)',
		verticalAlign: 'middle',
	},
});

/**
 * Pure presentational rendering of a skill tag lozenge. Kept separate from `SkillTag` so it can
 * also be rendered by `SkillHoverCardContent` without a runtime import cycle (`SkillTag` already
 * imports `SkillHoverCardContent` for its own hovercard).
 */
export const SkillTagInternal = ({
	triggerProps = {},
	invertColors,
	slug,
	IconComponent,
	onMouseEnter,
	onMouseLeave,
	color,
	shimmer,
	shouldWrapLabel,
}: {
	triggerProps?: Partial<TriggerProps>;
	onMouseEnter?: React.MouseEventHandler;
	onMouseLeave?: React.MouseEventHandler;
	IconComponent?: SkillIconComponent;
	shouldWrapLabel: boolean;
} & Omit<RovoSkillTagProps, 'iconName'>): JSX.Element => {
	const borderColor = getBorderColor(color, invertColors);
	const iconColor = getIconColor(color, invertColors);

	return (
		<Box
			as="span"
			data-inline-extension-layout={shouldWrapLabel ? 'flow' : undefined}
			xcss={cx(
				styles.parallelogram,
				!shouldWrapLabel && styles.borderAccent,
				shouldWrapLabel && styles.parallelogramWithWrapping,
				invertColors ? styles.backgroundInverse : styles.background,
			)}
			/* eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Border color is derived at runtime from skill metadata. */
			style={{ borderLeftColor: shouldWrapLabel ? undefined : borderColor }}
			onMouseEnter={onMouseEnter}
			onMouseLeave={onMouseLeave}
			{...triggerProps}
		>
			{shouldWrapLabel ? (
				<Box as="span" xcss={cx(styles.content, styles.contentWithWrapping)}>
					<Box
						as="span"
						xcss={styles.slashWithWrapping}
						/* eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Slash color is derived at runtime from skill metadata. */
						style={{ color: iconColor }}
					>
						/
					</Box>
					{IconComponent && (
						<Box as="span" xcss={styles.iconWithWrapping}>
							<Suspense fallback={null} interactionName="skill-tag-icon">
								<IconComponent label="" size="small" color={iconColor} />
							</Suspense>
						</Box>
					)}
					<Box as="span" xcss={styles.labelWithWrapping}>
						{shimmer ? (
							<ShimmerText as="span">{slug}</ShimmerText>
						) : (
							<Text color={invertColors ? 'color.text.inverse' : 'color.text'}>{slug}</Text>
						)}
					</Box>
				</Box>
			) : (
				<Inline xcss={styles.content} space="space.050" alignBlock="center">
					{IconComponent && (
						<Suspense fallback={null} interactionName="skill-tag-icon">
							<IconComponent label="" size="small" color={iconColor} />
						</Suspense>
					)}
					{shimmer ? (
						<ShimmerText>{slug}</ShimmerText>
					) : (
						<Text color={invertColors ? 'color.text.inverse' : 'color.text'}>{slug}</Text>
					)}
				</Inline>
			)}
		</Box>
	);
};
